import { useCallback, useEffect, useRef, useState } from "react";

export function useFitText({
    minFontSizePx = 6,
    maxFontSizePx = 16,
    resolution = 1,
    singleLine = false,
}: {
    minFontSizePx?: number;
    maxFontSizePx?: number;
    resolution?: number;
    singleLine?: boolean;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [fontSizePx, setFontSizePx] = useState(maxFontSizePx);

    const fit = useCallback(() => {
        const element = containerRef.current;
        if (!element) return;

        let low = minFontSizePx;
        let high = maxFontSizePx;
        let bestFit = maxFontSizePx;

        element.style.fontSize = `${maxFontSizePx}px`;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            element.style.fontSize = `${mid}px`;

            const isOverflowing = singleLine
                ? element.scrollWidth > element.clientWidth
                : element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;

            if (!isOverflowing) {
                bestFit = mid;
                low = mid + resolution;
            } else {
                high = mid - resolution;
            }
        }

        setFontSizePx(bestFit);
        element.style.fontSize = `${bestFit}px`;
    }, [minFontSizePx, maxFontSizePx, resolution, singleLine]);

    useEffect(() => {
        const observer = new ResizeObserver(fit);
        if (containerRef.current) observer.observe(containerRef.current);

        fit();

        return () => observer.disconnect();
    }, [fit]);

    return { ref: containerRef, fontSizePx };
}
